import "./ThinkingLog.css";
import { formatDurationClockShort } from "@ui/format.ts";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { HStack, VStack } from "@ui/Stack";
import { Heading } from "@ui/typography/Heading";
import { Text } from "@ui/typography/Text";
import { TextMono } from "@ui/typography/TextMono";
import { For, Match, Show, Switch } from "solid-js";
import type {
	PlanItemState,
	PlanSectionState,
	ThinkingLogEntry,
} from "../chatPanelState.ts";
import { useThinkingElapsedSeconds } from "./useThinkingElapsedSeconds.ts";

const planItemStatus = (
	plans: readonly PlanSectionState[] | undefined,
	id: string,
): PlanItemState["status"] | undefined => {
	if (!plans) {
		return undefined;
	}
	for (const plan of plans) {
		const item = plan.items.find((candidate) => candidate.id === id);
		if (item) {
			return item.status;
		}
	}
	return undefined;
};

const stepStatus = (props: {
	entry: ThinkingLogEntry;
	entryIndex: number;
	entryCount: number;
	live: boolean;
	plans?: readonly PlanSectionState[];
}): PlanItemState["status"] | undefined => {
	const fromPlan = planItemStatus(props.plans, props.entry.id);
	if (fromPlan === "complete" || fromPlan === "executing") {
		return fromPlan;
	}
	if (props.live && props.entryIndex === props.entryCount - 1) {
		return "executing";
	}
	return fromPlan;
};

const ThinkingLogStepStatus = (props: {
	status: PlanItemState["status"] | undefined;
}) => (
	<Show when={props.status === "executing" || props.status === "complete"}>
		<div class="thinking-log__status">
			<Switch>
				<Match when={props.status === "executing"}>
					<TextMono class="thinking-log__status-label" color="primary">
						running
					</TextMono>
				</Match>
				<Match when={props.status === "complete"}>
					<MaterialSymbol
						symbol="check"
						class="thinking-log__status-check"
						color="fg3"
						relativeSize="larger"
					/>
				</Match>
			</Switch>
		</div>
	</Show>
);

type ThinkingLogPlanEntry = Extract<ThinkingLogEntry, { kind: "plan" }>;
type ThinkingLogExecutionEntry = Extract<
	ThinkingLogEntry,
	{ kind: "execution" }
>;

const logEntrySymbol = (kind: ThinkingLogEntry["kind"]) =>
	kind === "plan" ? "psychology" : "sort";

const hasExecutionContent = (entry: ThinkingLogExecutionEntry) =>
	entry.title.length > 0 ||
	entry.description.length > 0 ||
	entry.images.length > 0 ||
	entry.socialAccounts.length > 0;

const ThinkingLogPlanContent = (props: { entry: ThinkingLogPlanEntry }) => {
	return (
		<VStack>
			<Show when={props.entry.title}>
				<Heading as="h4" class="thinking-log__title">{props.entry.title}</Heading>
			</Show>
			<Show when={props.entry.description}>
				<Text size="small" color="fg2">
					{props.entry.description}
				</Text>
			</Show>
		</VStack>
	);
};

const ThinkingLogExecutionContent = (props: {
	entry: ThinkingLogExecutionEntry;
}) => {
	return (
		<VStack class="thinking-log__results-content">
			<Show when={props.entry.title}>
				<Heading as="h4" class="thinking-log__title">{props.entry.title}</Heading>
			</Show>
			<Show when={props.entry.description}>
				<Text size="small" color="fg2">
					{props.entry.description}
				</Text>
			</Show>
			<Show
				when={
					props.entry.images.length > 0 ||
					props.entry.socialAccounts.length > 0
				}
			>
				<div class="thinking-log__results-container">
					<HStack spacing="small" align="start" class="thinking-log__results">
						<Show when={props.entry.images.length > 0}>
							<div class="thinking-log__images-container">
								<div class="thinking-log__images">
									<For each={props.entry.images}>
										{(image) => (
											<img
												class="thinking-log__image"
												src={image.url}
												alt=""
												loading="lazy"
												decoding="async"
											/>
										)}
									</For>
								</div>
							</div>
						</Show>
						<Show when={props.entry.socialAccounts.length > 0}>
							<div class="thinking-log__social-accounts-container">
								<div class="thinking-log__social-accounts">
									<For each={props.entry.socialAccounts}>
										{(account) => (
											<div class="thinking-log__social-account">
												<img
													class="thinking-log__profile-picture"
													src={account.profile_picture_url}
													alt={account.handle ? `@${account.handle}` : ""}
													loading="lazy"
													decoding="async"
												/>
											</div>
										)}
									</For>
								</div>
							</div>
						</Show>
					</HStack>
				</div>
			</Show>
		</VStack>
	);
};

export const ThinkingLog = (props: {
	entries: readonly ThinkingLogEntry[];
	startedAtMs: number;
	thinkingEndedAtMs?: number;
	/** Stream in progress: show step status and omit the finished duration header. */
	live?: boolean;
	plans?: readonly PlanSectionState[];
}) => {
	const displaySeconds = useThinkingElapsedSeconds(props);
	const live = () => props.live === true;
	const entryCount = () => props.entries.length;

	return (
		<VStack
			spacing="large"
			class="thinking-log"
			classList={{ "thinking-log--live": live() }}
		>
			<Show when={!live()}>
				<div class="thinking-log__duration-container">
					<HStack spacing="medium" class="thinking-log__duration">
						<MaterialSymbol symbol="schedule" relativeSize="larger" color="fg4" />
						<TextMono color="fg2">
							Thought for {formatDurationClockShort(displaySeconds())}
						</TextMono>
					</HStack>
				</div>
			</Show>
			<Show when={props.entries.length > 0}>
				<For each={props.entries}>
					{(entry, index) => (
						<Switch>
							<Match when={entry.kind === "plan" ? entry : undefined}>
								{(planEntry) => (
									<HStack
										spacing="medium"
										align="start"
										class="thinking-log__entry"
									>
										<MaterialSymbol
											symbol={logEntrySymbol(planEntry().kind)}
											relativeSize="even-larger"
											color="primary"
											class="thinking-log__icon"
										/>
										<ThinkingLogPlanContent entry={planEntry()} />
										<Show when={live()}>
											<ThinkingLogStepStatus
												status={stepStatus({
													entry: planEntry(),
													entryIndex: index(),
													entryCount: entryCount(),
													live: true,
													plans: props.plans,
												})}
											/>
										</Show>
									</HStack>
								)}
							</Match>
							<Match
								when={
									entry.kind === "execution" && hasExecutionContent(entry)
										? entry
										: undefined
								}
							>
								{(executionEntry) => (
									<HStack
										spacing="medium"
										align="start"
										class="thinking-log__entry"
									>
										<MaterialSymbol
											symbol={logEntrySymbol(executionEntry().kind)}
											relativeSize="even-larger"
											color="primary"
											class="thinking-log__icon"
										/>
										<ThinkingLogExecutionContent entry={executionEntry()} />
										<Show when={live()}>
											<ThinkingLogStepStatus
												status={stepStatus({
													entry: executionEntry(),
													entryIndex: index(),
													entryCount: entryCount(),
													live: true,
													plans: props.plans,
												})}
											/>
										</Show>
									</HStack>
								)}
							</Match>
						</Switch>
					)}
				</For>
			</Show>
		</VStack>
	);
};
